/**
 * Card storage — Netlify API with localStorage fallback.
 * Works on all devices (iPhone Safari, Android, desktop).
 */

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || 'https://pay.cronyl.com';
const LS_KEY = 'cronyl_pay_cards';

export interface StoredCard {
  id: string;
  network: string;
  coin: string;
  host: string;
  address: string;
  tag?: string | null;
  memo?: string | null;
  configured?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

function readLocal(): Record<string, StoredCard> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeLocal(cards: Record<string, StoredCard>) {
  localStorage.setItem(LS_KEY, JSON.stringify(cards));
}

async function apiRequest(path: string, options: RequestInit = {}) {
  const res = await fetch(`${API_ORIGIN}/api${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || `Request failed (${res.status})`);
  return data;
}

export async function loadCard(cardId: string): Promise<StoredCard | null> {
  if (!cardId) return null;

  try {
    const data = await apiRequest(`/load-card?id=${encodeURIComponent(cardId)}`);
    if (data?.card?.address) {
      const cards = readLocal();
      cards[cardId] = data.card;
      writeLocal(cards);
      return data.card;
    }
  } catch {
    /* API unavailable — try local */
  }

  const local = readLocal()[cardId];
  return local?.address ? local : null;
}

export async function saveCard(
  cardId: string,
  data: Omit<StoredCard, 'id' | 'configured' | 'createdAt' | 'updatedAt'>,
): Promise<StoredCard> {
  if (!cardId) throw new Error('Card ID is missing.');

  const payload = {
    id: cardId,
    network: data.network.trim().toLowerCase(),
    coin: data.coin.trim().toUpperCase(),
    host: data.host.trim(),
    address: data.address.trim(),
    tag: data.tag?.trim() || null,
    memo: data.memo?.trim() || null,
  };

  try {
    const result = await apiRequest('/save-card', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (result?.card) {
      const cards = readLocal();
      cards[cardId] = result.card;
      writeLocal(cards);
      return result.card;
    }
  } catch (err) {
    /* If already configured, rethrow */
    if (err instanceof Error && err.message.includes('already configured')) throw err;
    /* fall through to local */
  }

  const now = new Date().toISOString();
  const existing = readLocal()[cardId];
  if (existing?.configured && existing?.address) {
    throw new Error('This card is already configured and cannot be changed.');
  }

  const card: StoredCard = {
    id: cardId,
    ...payload,
    configured: true,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  };

  const cards = readLocal();
  cards[cardId] = card;
  writeLocal(cards);
  return card;
}

export function buildCardUrl(cardId: string): string {
  const payOrigin = import.meta.env.VITE_PAY_ORIGIN || 'https://pay.cronyl.com';
  return `${payOrigin}/c/${encodeURIComponent(cardId)}`;
}
